import i18next from "i18next";
import { useCallback, useState } from "react";
import { ItemService } from "../../services/item.service";
import { getActiveBusiness } from "../../store/session";
import {
  CreateItemType,
  ItemType,
  PaginationType,
  UpdateItemSKUType,
} from "../../types/item";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BrowserRouterConstants } from "../../utils/constants";
import { processNewItems } from "../../utils/items";
import { validateDuplicateSku } from "../../utils/excel";

const useContainer = () => {
  //#region ----- local states
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [currentItem, setCurrentItem] = useState<ItemType>();
  const [inventoryItems, setInventoryItems] = useState<ItemType[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    limit: 20,
    max: 1,
  });
  const [hasMore, setHasMore] = useState(true);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [allSku, setAllSku] = useState<string[]>([]);
  const [allUOMs, setAllUOMs] = useState<string[]>([]);
  const [excelErrors, setExcelErrors] = useState<any[]>([]);
  const [imageLocations, setImageLocations] = useState<Array<File | string>>(
    []
  );
  const [videoFile, setVideoFile] = useState<File | string>();
  const [flexiColumns, setFlexiColumns] = useState<string[]>();
  const [categories, setCategories] = useState<string[]>([]);
  const [databaseSku, setDatabaseSku] = useState<string[]>([]);
  const [duplicateSKU, setDuplicateSKU] = useState<{
    processedData: CreateItemType[];
    duplicates: string[];
  }>();
  const [newItems, setNewItems] = useState<string[]>([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState<{
    show: boolean;
    useDatabaseSku: boolean;
    onClick: (data: ItemType[]) => void;
  }>({ show: false, useDatabaseSku: true, onClick: () => {} });

  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const duplicates: string[] = [];

  //#region ----- misc
  const fetchInventoryItems = useCallback(
    ({
      businessId,
      paging = true,
      limit = 20,
      offset = 0,
      sortBy = "updatedAt",
      sortDirection = "desc",
      searchTerm,
      inventoryType,
      isReset = true,
    }: {
      businessId: string;
      paging?: boolean;
      limit?: number;
      offset?: number;
      sortBy?: string;
      sortDirection?: "asc" | "desc";
      searchTerm?: string | null;
      inventoryType?: string | null;
      isReset?: boolean;
    }) => {
      ItemService.getInventoryItems({
        businessId,
        paging,
        limit,
        offset,
        sortBy,
        sortDirection,
        searchTerm,
        inventoryType,
      })
        .then((res) => {
          setShowAlert(false);
          if (isReset) {
            setInventoryItems(res.data);
            setHasMore(true);
          } else {
            setInventoryItems((prev) => [...prev, ...res.data]);
          }

          if (res.pagination) {
            setPagination(res.pagination);
          }

          if (res.data.length === 0) {
            setHasMore(false);
          }
        })
        .catch((err) => console.error(err))
        .finally(() => setIsLoading(false));
    },
    []
  );

  const fetchCurrentItem = useCallback(
    ({ inventoryItemId }: { inventoryItemId: string }) => {
      ItemService.getInventoryItemById({
        id: inventoryItemId,
      })
        .then((res) => {
          setShowAlert(false);

          if (searchParams.get("mode") === "create") {
            res.sku = "";
            res.productName = "";
            res.productDescription = "";
            res.inventoryType = "";
          }
          setCurrentItem(res);
        })
        .catch((err) => console.error(err));
    },
    []
  );

  const fetchInventoryFormData = (
    businessId: string,
    inventoryItemId?: string
  ) => {
    setIsLoading(true);
    inventoryItemId && fetchCurrentItem({ inventoryItemId });
    ItemService.getInventoryItemField({
      fields: ["uom1", "uom2", "uom3", "tags", "sku"],
      businessId,
      isDistinct: false,
    })
      .then((res) => {
        const uoms: string[] = [];
        const tags: string[] = [];

        for (let i = 0; i < res.length; i++) {
          if (res[i].tags) {
            tags.push(...res[i].tags.tags);
          }

          for (const key in res[i]) {
            if (
              res[i][key] !== undefined &&
              res[i][key] !== null &&
              !uoms.includes(res[i][key]) &&
              key !== "tags" &&
              key !== "sku" &&
              res[i][key] !== ""
            ) {
              uoms.push(res[i][key]);
            }
          }
        }

        setAllTags(Array.from(new Set(tags)));
        setAllUOMs(uoms);
        setAllSku(res.map((item: any) => item.sku));
      })
      .catch((error) => console.error(error))
      .finally(() => setIsLoading(false));
  };

  const handleItemFlexiCols = (items: ItemType[], flexiCols?: string[]) => {
    const allFlexiColumns: string[] = [];
    for (const item of items) {
      const flexiData = item.flexiColumns?.data || [];
      for (const column of flexiData) {
        if (!allFlexiColumns.includes(column.columnName)) {
          allFlexiColumns.push(column.columnName);
        }
      }
    }

    const tableData: Array<string | number>[] = [];

    if (flexiCols) {
      for (const item of items) {
        // defined the fixed values
        const fixedValues = [
          item.sku,
          item.productName,
          item.quantity ?? 0,
          item.currency,
          item.price,
          item.uom1 ?? "unit",
          item.inventoryType,
        ];

        // get the flexi values
        const flexiValues = [];
        for (const column of flexiCols) {
          const flexiData = item.flexiColumns?.data || [];
          const value =
            flexiData.find((d) => d.columnName === column)?.cellValue || "n/a";
          flexiValues.push(value);
        }

        tableData.push([...fixedValues]);
      }
    }

    return {
      allFlexiColumns: allFlexiColumns,
      tableData: tableData,
    };
  };

  const splitIntoChunks = (array: any, chunkSize: number) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const deleteInventoryItemBatch = (ids: string[]) => {
    setIsLoading(true);
    ItemService.deleteInventoryItemBatch({ ids })
      .then((res) => {
        if (res) {
          setAlertMessage(
            t("catalog.inventory.removeItemsSuccessModalLabel", {
              selected: ids.length,
            })
          );
          setShowAlert(true);

          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoading(false));
  };

  const verifySKUReminder = () => {
    const reminder = localStorage.getItem("duplicateSKUReminder");

    if (reminder) {
      const currentTime = new Date().getTime();
      const hoursDifference = (currentTime - +reminder) / (1000 * 60 * 60);

      return hoursDifference > 24;
    }

    return true;
  };

  const initiateInventoryItems = useCallback((businessId: string) => {
    ItemService.getInventoryItems({
      businessId: businessId,
      paging: true,
      limit: 20,
      offset: 0,
      sortBy: "updatedAt",
    })
      .then((itemRes) => {
        setShowAlert(false);
        setInventoryItems(itemRes.data);
        if (itemRes.pagination) {
          setPagination(itemRes.pagination);
        }

        processNewItems(
          "already_seen_items",
          itemRes.data,
          (noteAlreadySeenItems) => {
            setNewItems(noteAlreadySeenItems);
          }
        );

        return ItemService.getInventoryItemField({
          fields: ["flexiColumns", "inventoryType"],
          businessId,
        });
      })
      .then((filterRes) => {
        const { allFlexiColumns } = handleItemFlexiCols(filterRes);
        setFlexiColumns(allFlexiColumns);

        const cats: string[] = [];
        for (const element of filterRes) {
          cats.push(element.inventoryType);
        }

        const uniqueValues = cats.filter(
          (item, index) => cats.indexOf(item) === index
        );
        setCategories(uniqueValues);

        return ItemService.getInventoryItemField({
          fields: ["id", "sku", "productName"],
          businessId,
          isDistinct: false,
        });
      })
      .then((skuRes) => {
        const allSku = skuRes.map((item: ItemType) => item.sku);
        setDatabaseSku(allSku);
        const validatedDuplicates = validateDuplicateSku(
          skuRes as CreateItemType[]
        );

        if (validatedDuplicates.length > 0 && verifySKUReminder()) {
          setDuplicateSKU({
            processedData: skuRes as CreateItemType[],
            duplicates: validatedDuplicates,
          });
          setShowDuplicateModal({
            show: true,
            useDatabaseSku: false,
            onClick: (data: ItemType[]) => {
              setIsLoading(true);
              handleUpdateSku(businessId, data);
            },
          });
          localStorage.setItem(
            "duplicateSKUReminder",
            new Date().getTime().toString()
          );
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoading(false));
  }, []);

  //#region ----- handler functions
  const handlePaging = (action: string | number) => {
    const pagingConfig = {
      limit: pagination.limit,
      offset: 0,
    };

    if (typeof action === "string") {
      switch (action) {
        case "first":
          pagingConfig.offset = 0;
          break;

        case "previous":
          pagingConfig.offset = pagination.limit * (pagination.current - 2);
          break;

        case "next":
          pagingConfig.offset = pagination.limit * pagination.current;
          break;

        case "last":
          pagingConfig.offset =
            pagination.max * pagination.limit - pagination.limit;
          break;
      }
    } else {
      pagingConfig.offset = (action - 1) * pagination.limit;
    }

    const businessId = getActiveBusiness()?.id;
    if (businessId) {
      try {
        fetchInventoryItems({
          businessId: businessId,
          paging: true,
          ...pagingConfig,
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleExcelUpload = async (data: CreateItemType[]) => {
    const chunks = splitIntoChunks(data, 200);
    const uploadPromises = chunks.map(async (chunk) => {
      try {
        await ItemService.createBatchInventoryItems({
          items: chunk,
        });
      } catch (error: any) {
        console.error(error);
        handleError(error.message);
      }
    });

    await Promise.all(uploadPromises);
    setShowAlert(true);
    setAlertMessage(i18next.t("catalog.inventory.excelUploaded"));
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  const handleUpdateSku = async (businessId: string, data: ItemType[]) => {
    const chunks = splitIntoChunks(data, 200);
    const uploadPromises = chunks.map(async (chunk) => {
      try {
        await ItemService.patchInventoryItemSKU({
          businessId,
          updateSkuDto: chunk as UpdateItemSKUType[],
        });
      } catch (error: any) {
        console.error(error);
        handleError(error.message);
      }
    });

    await Promise.all(uploadPromises);
    setShowAlert(true);
    setAlertMessage(
      i18next.t("catalog.inventory.editForm.inventoryItemUpdated")
    );
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  /**
   * upload excel file to backend API, filter out errors
   * @param acceptedFiles
   */
  const onDrop = async (acceptedFiles: any) => {
    if (acceptedFiles.length > 0) {
      setIsLoading(true);
      try {
        const importResponse = await ItemService.importInventoryItems(
          acceptedFiles[0]
        );

        if (importResponse.data.invalidItems) {
          // find items with error that's not Duplicate SKU error code
          const invalidItemErrors = importResponse.data.invalidItems.filter(
            (invalidItem) => {
              const nonDuplicateError = invalidItem.errors?.find(
                (error) => error.error !== "Duplicate SKU"
              );

              return !!nonDuplicateError;
            }
          );

          // put the errors in excelErrors state and show fail modal
          const itemErrors = invalidItemErrors.map((invalidItemError) => {
            const formattedErrors = invalidItemError.errors?.map((error) => {
              const fieldNames = error.fields.map((field) => {
                return error.error === "Duplicate SKU"
                  ? field
                  : t(`catalog.items.${field}`);
              });

              return {
                code: error.error,
                message: t("catalog.inventory.import.error.errorOnField", {
                  code: error.error,
                  field: fieldNames.join(", "),
                }),
              };
            });
            return {
              name: `row ${invalidItemError.row}`,
              errors: formattedErrors,
            };
          });
          setExcelErrors(itemErrors);

          // exit if there are other errors aside from Duplicate SKU
          if (itemErrors.length > 0) {
            setIsLoading(false);
            return;
          }

          navigate(
            `/client/${BrowserRouterConstants.InventoryExportImport}/duplicate/${importResponse.data.draftId}`,
            {}
          );
        } else {
          const totalItems =
            (importResponse.data?.added ?? 0) +
            (importResponse.data?.updated ?? 0);
          const message = t(
            importResponse.data?.updated ?? 0 > 0
              ? "catalog.inventory.import.success.updatedInventory"
              : "catalog.inventory.import.success.addInventory",
            {
              totalItems: totalItems,
            }
          );
          setAlertMessage(message);

          setShowAlert(true);
          setTimeout(() => {
            window.location.href = `/client/${BrowserRouterConstants.Inventory}`;
          }, 3000);
        }
      } catch (error: any) {
        console.error("Error uploading file:", error);
        const errorMessage =
          error?.response?.data?.message || error?.message || "";

        if (errorMessage.includes("Missing")) {
          setExcelErrors([
            {
              name: "template",
              errors: [],
            },
          ]);
        } else if (errorMessage.includes("No data in excel")) {
          setExcelErrors([
            {
              name: "noData",
              errors: [],
            },
          ]);
        } else {
          alert(error.message);
        }

        setIsLoading(false);
      }

      // setIsLoading(true);
      // var reader = new FileReader();
      // reader.readAsArrayBuffer(acceptedFiles[0]);
      // reader.onload = function (event) {
      //   if (reader.result) {
      //     const fileContent = event.target?.result;
      //     const workbook = XLSX.read(fileContent, { type: "binary" });

      //     const sheetName = workbook.SheetNames[0];
      //     const excelArray: Array<Array<string | number>> =
      //       XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      //         header: 1,
      //         blankrows: false,
      //       });
      //     const validatedExcel = validateExcel(excelArray);

      //     if (validatedExcel.isValid) {
      //       const { data } = convertExcelToInventoryItems(
      //         excelArray,
      //         getActiveBusiness(),
      //         session?.account.id
      //       );

      //       if (data.length > 0) {
      //         const duplicates = validateDuplicateSku(data, databaseSku);
      //         // return;
      //         if (duplicates.length > 0) {
      //           setDuplicateSKU({ processedData: data, duplicates });
      //           setShowDuplicateModal({
      //             show: true,
      //             useDatabaseSku: true,
      //             onClick: (data: CreateItemType[]) => {
      //               setIsLoading(true);
      //               handleExcelUpload(data);
      //             },
      //           });
      //         } else {
      //           handleExcelUpload(data);
      //         }
      //       } else {
      //         handleError(t("catalog.inventory.invalidExcel"));
      //       }
      //     } else {
      //       handleError(
      //         validatedExcel.message ?? t("catalog.inventory.invalidExcel")
      //       );
      //     }
      //   }
      // };
    }
  };

  const handleError = (message: string) => {
    setIsLoading(false);
    setAlertMessage(message);
    setShowToast(true);

    setTimeout(() => {
      setShowToast(false);
    }, 2000);
  };

  return {
    handlePaging,
    handleExcelUpload,
    handleError,
    handleUpdateSku,
    handleItemFlexiCols,
    fetchInventoryItems,
    fetchCurrentItem,
    fetchInventoryFormData,
    deleteInventoryItemBatch,
    initiateInventoryItems,
    isLoading,
    setIsLoading,
    showAlert,
    setShowAlert,
    alertMessage,
    setAlertMessage,
    showCreateModal,
    setShowCreateModal,
    pagination,
    setPagination,
    inventoryItems,
    setInventoryItems,
    searchTerm,
    setSearchTerm,
    showToast,
    setShowToast,
    currentItem,
    setCurrentItem,
    allTags,
    setAllTags,
    allSku,
    setAllSku,
    allUOMs,
    setAllUOMs,
    hasMore,
    setHasMore,
    imageLocations,
    setImageLocations,
    videoFile,
    setVideoFile,
    excelErrors,
    setExcelErrors,
    flexiColumns,
    setFlexiColumns,
    categories,
    setCategories,
    databaseSku,
    setDatabaseSku,
    duplicateSKU,
    setDuplicateSKU,
    newItems,
    setNewItems,
    showDuplicateModal,
    setShowDuplicateModal,
    duplicates,
    onDrop,
  };
};

export default useContainer;
